import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * UI State Management
 */
interface UINotification {
    id: string;
    type: 'success' | 'error' | 'warning' | 'info';
    message: string;
}

interface UIStore {
    // Sidebar
    isSidebarOpen: boolean;
    toggleSidebar: () => void;
    setSidebarOpen: (isOpen: boolean) => void;

    // Notifications
    notifications: UINotification[];
    addNotification: (notification: Omit<UINotification, 'id'>) => void;
    removeNotification: (id: string) => void;

    // Theme
    theme: 'light' | 'dark';
    toggleTheme: () => void;
}

export const useUIStore = create<UIStore>()(
    devtools(
        persist(
            (set) => ({
                // Sidebar Init
                isSidebarOpen: true,
                toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
                setSidebarOpen: (isOpen) => set({ isSidebarOpen: isOpen }),

                // Notifications Init
                notifications: [],
                addNotification: (notification) => {
                    const id = Math.random().toString(36).substring(2, 9);
                    set((state) => ({
                        notifications: [...state.notifications, { ...notification, id }],
                    }));
                },
                removeNotification: (id) =>
                    set((state) => ({
                        notifications: state.notifications.filter((n) => n.id !== id),
                    })),

                // Theme Init
                theme: 'light',
                toggleTheme: () =>
                    set((state) => ({
                        theme: state.theme === 'light' ? 'dark' : 'light',
                    })),
            }),
            {
                name: 'ui-storage',
                partialize: (state) => ({ theme: state.theme }), // Only persist theme
            }
        )
    )
);
